import { useState, useEffect } from "react";
import { IconBuildingStore, IconPencil, IconPlus } from "@tabler/icons-react";
import { axiosInstance } from "../../../utils/axiosInstance";
import { Location, SiteStatus } from "../../../interfaces/interfaces";
import EditLocationModal from "./EditLocationModal";
import AddLocation from "../addLocation/AddLocation";
import Message from "../../Alert/Message";
import { Card, EmptyState, LoadingPanel, Pill, Tone } from "../../ui";

function resolveLocationStatus(location: Location): SiteStatus {
  if (location.site_status) {
    return location.site_status;
  }
  return location.is_coming_soon ? "coming_soon" : "active";
}

const STATUS_LABELS: Record<SiteStatus, string> = {
  active: "Active",
  coming_soon: "Coming soon",
  closed: "Closed",
};

const STATUS_TONES: Record<SiteStatus, Tone> = {
  active: "good",
  coming_soon: "info",
  closed: "neutral",
};

export default function LocationsList() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string | boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);

  useEffect(() => {
    fetchLocations();
  }, [refresh]);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/location");
      // Sort locations by locationPin (numeric if possible, otherwise alphabetical)
      const sortedLocations = response.data.sort((a: Location, b: Location) => {
        const pinA = a.locationPin || "";
        const pinB = b.locationPin || "";

        // Try numeric comparison first
        const numA = Number(pinA);
        const numB = Number(pinB);

        if (!isNaN(numA) && !isNaN(numB) && pinA !== "" && pinB !== "") {
          return numA - numB;
        }

        // Fallback to string comparison
        return pinA.localeCompare(pinB);
      });
      setLocations(sortedLocations);
    } catch (error) {
      console.error("Error fetching locations:", error);
      setMessage("Failed to load locations");
    }
    setLoading(false);
  };

  const handleEdit = (location: Location) => {
    setSelectedLocation(location);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedLocation(null);
  };

  const handleSaveSuccess = (msg: string) => {
    setMessage(msg);
    setRefresh(!refresh);
    handleCloseModal();
  };

  const handleCreated = (msg: string) => {
    setMessage(msg);
    setRefresh(!refresh);
    setAddOpen(false);
  };

  return (
    <>
      <Message message={message} setMessage={setMessage} />

      <Card
        flush
        title="Locations"
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setAddOpen(true)}
          >
            <IconPlus size={16} />
            Add location
          </button>
        }
      >
        {loading ? (
          <LoadingPanel label="Loading locations" rows={6} />
        ) : locations.length > 0 ? (
          <div className="sq-table-wrap">
            <table className="sq-table is-stacked">
              <thead>
                <tr>
                  <th scope="col">Location</th>
                  <th scope="col">Address</th>
                  <th scope="col">CMMS</th>
                  <th scope="col">Status</th>
                  <th scope="col">Alerts</th>
                  <th scope="col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => {
                  const status = resolveLocationStatus(location);
                  return (
                    <tr key={location.locationId}>
                      <td className="cell-title">
                        <div className="sq-strong">{location.name}</div>
                        <div className="sq-muted">{location.locationPin}</div>
                      </td>
                      <td data-label="Address" className="cell-wide">
                        <div>{location.address}</div>
                        <div className="sq-muted">{location.post_code}</div>
                      </td>
                      <td data-label="CMMS">
                        {location.nitro_cmms_location_id != null ? (
                          <Pill tone="good">Mapped</Pill>
                        ) : (
                          <span className="sq-muted">—</span>
                        )}
                      </td>
                      <td data-label="Status">
                        <Pill tone={STATUS_TONES[status]}>{STATUS_LABELS[status]}</Pill>
                      </td>
                      <td data-label="Alerts">
                        <Pill tone={location.alerts_enabled ? "good" : "neutral"}>
                          {location.alerts_enabled ? "Enabled" : "Disabled"}
                        </Pill>
                      </td>
                      <td className="cell-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleEdit(location)}
                          aria-label={`Edit ${location.name}`}
                        >
                          <IconPencil size={16} />
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={<IconBuildingStore size={24} />} title="No locations found">
            Add your first location to get started.
          </EmptyState>
        )}
      </Card>

      {selectedLocation && (
        <EditLocationModal
          open={modalOpen}
          location={selectedLocation}
          onClose={handleCloseModal}
          onSaveSuccess={handleSaveSuccess}
        />
      )}

      <AddLocation open={addOpen} onClose={() => setAddOpen(false)} onCreated={handleCreated} />
    </>
  );
}
